import React, { createContext, useContext, useEffect, useMemo, useState } from 'react';

const ContentContext = createContext();

const STORAGE_KEYS = {
  products: 'mystic_prana_products',
  gallery: 'mystic_prana_gallery'
};

const LEGACY_PRODUCT_CATEGORY_MAP = {
  'aura-clearing': 'energy-clearing'
};

export const normalizeProductCategory = (category) => {
  if (!category) return category;
  return LEGACY_PRODUCT_CATEGORY_MAP[category] || category;
};

export const PRODUCT_CATEGORIES = [
  { value: 'all', label: 'All Products' },
  { value: 'energy-clearing', label: 'Energy Clearing' },
  { value: 'crystals', label: 'Crystals & Stones' },
  { value: 'meditation', label: 'Meditation' },
  { value: 'aromatherapy', label: 'Aromatherapy' },
  { value: 'sound-healing', label: 'Sound Healing' },
  { value: 'cleansing', label: 'Space Cleansing' }
];

export const GALLERY_CATEGORIES = [
  { value: 'all', label: 'All Photos' },
  { value: 'meditation', label: 'Meditation' },
  { value: 'healing', label: 'Healing Sessions' },
  { value: 'community', label: 'Community' },
  { value: 'spiritual', label: 'Spiritual Ceremonies' }
];

const defaultProducts = [
  {
    id: 1,
    name: 'Energy Mist',
    category: 'energy-clearing',
    price: '₹600',
    image: '',
    description: 'An instant mist to clear stagnant, low-vibrational energy from your personal space and field.',
    rating: 4.9,
    reviews: 142,
    inStock: true
  },
  {
    id: 2,
    name: 'Meditation Cushion & Mat Set',
    category: 'meditation',
    price: '725.00',
    image: '',
    description: 'Comfortable meditation set for deeper spiritual practice.',
    rating: 4.9,
    reviews: 89,
    inStock: true
  }
];

const defaultGallery = [];

const loadData = (key, fallback) => {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) {
      return fallback;
    }
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : fallback;
  } catch (error) {
    console.warn(`[Content] Failed to parse ${key}:`, error);
    return fallback;
  }
};

export const ContentProvider = ({ children }) => {
  const [products, setProducts] = useState(() =>
    loadData(STORAGE_KEYS.products, defaultProducts).map((item) => ({
      ...item,
      category: normalizeProductCategory(item.category)
    }))
  );
  const [galleryItems, setGalleryItems] = useState(() => loadData(STORAGE_KEYS.gallery, defaultGallery));

  useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.products, JSON.stringify(products));
  }, [products]);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEYS.gallery, JSON.stringify(galleryItems));
  }, [galleryItems]);

  const value = useMemo(() => {
    const createProduct = (product) => {
      setProducts((prev) => [
        {
          ...product,
          category: normalizeProductCategory(product.category),
          id: product.id || `prod_${Date.now()}`
        },
        ...prev
      ]);
    };

    const updateProduct = (id, updates) => {
      setProducts((prev) =>
        prev.map((item) =>
          item.id === id
            ? { ...item, ...updates, category: normalizeProductCategory(updates.category || item.category) }
            : item
        )
      );
    };

    const deleteProduct = (id) => {
      setProducts((prev) => prev.filter((item) => item.id !== id));
    };

    const createGalleryItem = (item) => {
      setGalleryItems((prev) => [{ ...item, id: item.id || `gallery_${Date.now()}` }, ...prev]);
    };

    const updateGalleryItem = (id, updates) => {
      setGalleryItems((prev) => prev.map((item) => (item.id === id ? { ...item, ...updates } : item)));
    };

    const deleteGalleryItem = (id) => {
      setGalleryItems((prev) => prev.filter((item) => item.id !== id));
    };

    return {
      products,
      galleryItems,
      createProduct,
      updateProduct,
      deleteProduct,
      createGalleryItem,
      updateGalleryItem,
      deleteGalleryItem
    };
  }, [products, galleryItems]);

  return <ContentContext.Provider value={value}>{children}</ContentContext.Provider>;
};

export const useContent = () => {
  const context = useContext(ContentContext);
  if (!context) {
    throw new Error('useContent must be used within a ContentProvider');
  }
  return context;
};
